import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { Navigate } from "react-router-dom";
import { endpoints } from "../api.js";
import { legalUrl } from "../config.js";
import { Icon } from "../components/Icon.jsx";
import { Banner, ErrorBanner, Loading, Spinner } from "../components/ui.jsx";
import { useDocumentTitle, useErrorMessage, useLocale, useLocalePath } from "../i18n/hooks.js";
import { AuthShell } from "../layouts.jsx";
import { countryOptions } from "../lib/countries.js";
import { setStoredTenantId } from "../lib/storage.js";
import { useSession } from "../session.jsx";

const STEPS = ["org", "terms", "plan"];

function Stepper({ current }) {
  const idx = STEPS.indexOf(current);
  return (
    <ol className="stepper">
      {STEPS.map((s, i) => (
        <li key={s} className={i < idx ? "is-done" : i === idx ? "is-current" : ""}>
          <span className="stepper-dot">{i < idx ? <Icon name="check" size={13} strokeWidth={2.4} /> : i + 1}</span>
          <span className="stepper-label">
            <FormattedMessage id={`onboarding.step.${s}`} />
          </span>
        </li>
      ))}
    </ol>
  );
}

function OrgStep({ onDone }) {
  const intl = useIntl();
  const locale = useLocale();
  const errMsg = useErrorMessage();
  const countries = useMemo(() => countryOptions(locale), [locale]);
  const [form, setForm] = useState({ name: "", country: "", vatId: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { name: form.name.trim(), country: form.country };
      if (form.vatId.trim()) body.vatId = form.vatId.trim();
      const { tenant } = await endpoints.createTenant(body);
      setStoredTenantId(tenant.id);
      await onDone(tenant);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <form className="form" onSubmit={submit}>
      <h2 className="h2">
        <FormattedMessage id="onboarding.org.title" />
      </h2>
      <p className="muted">
        <FormattedMessage id="onboarding.org.body" />
      </p>
      <ErrorBanner message={error ? errMsg(error) : null} />
      <label className="field">
        <span className="field-label">
          <FormattedMessage id="onboarding.org.name" />
        </span>
        <input required maxLength={200} value={form.name} onChange={set("name")} autoComplete="organization" />
      </label>
      <div className="field-row">
        <label className="field">
          <span className="field-label">
            <FormattedMessage id="onboarding.org.country" />
          </span>
          <select required value={form.country} onChange={set("country")}>
            <option value="" disabled>
              {intl.formatMessage({ id: "onboarding.org.countryPlaceholder" })}
            </option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">
            <FormattedMessage id="onboarding.org.vat" />
            <span className="field-opt">
              <FormattedMessage id="common.optional" />
            </span>
          </span>
          <input value={form.vatId} onChange={set("vatId")} maxLength={32} />
        </label>
      </div>
      <button type="submit" className="btn btn-primary" disabled={busy || !form.name.trim() || !form.country}>
        {busy ? <Spinner size={16} /> : null}
        <FormattedMessage id="common.continue" />
      </button>
    </form>
  );
}

function TermsStep({ onDone, isOwner }) {
  const locale = useLocale();
  const errMsg = useErrorMessage();
  const [versions, setVersions] = useState(null);
  const [checked, setChecked] = useState({ tos: false, aup: false, dpa: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    endpoints.legalVersions().then(setVersions, setError);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { tenant } = await endpoints.acceptTerms({ tos: versions.tos, aup: versions.aup, dpa: versions.dpa });
      await onDone(tenant);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const all = checked.tos && checked.aup && checked.dpa;

  return (
    <form className="form" onSubmit={submit}>
      <h2 className="h2">
        <FormattedMessage id="onboarding.terms.title" />
      </h2>
      <p className="muted">
        <FormattedMessage id="onboarding.terms.body" />
      </p>
      <ErrorBanner message={error ? errMsg(error) : null} />
      {!isOwner ? (
        <Banner kind="warn" icon="lock">
          <FormattedMessage id="onboarding.ownerOnly" />
        </Banner>
      ) : null}
      {!versions && !error ? <Loading /> : null}
      {versions ? (
        <div className="check-list">
          {["tos", "aup", "dpa"].map((doc) => (
            <label key={doc} className={`check-card ${checked[doc] ? "is-checked" : ""}`}>
              <input
                type="checkbox"
                checked={checked[doc]}
                disabled={!isOwner}
                onChange={(e) => setChecked((c) => ({ ...c, [doc]: e.target.checked }))}
              />
              <span className="check-body">
                <span className="check-title">
                  <FormattedMessage
                    id={`onboarding.terms.${doc}`}
                    values={{
                      link: (chunks) => (
                        <a href={legalUrl(doc, locale)} target="_blank" rel="noopener noreferrer">
                          {chunks}
                          <Icon name="external" size={13} />
                        </a>
                      ),
                    }}
                  />
                </span>
                <span className="muted small">
                  <FormattedMessage id="onboarding.terms.version" values={{ version: versions[doc] }} />
                </span>
              </span>
            </label>
          ))}
        </div>
      ) : null}
      <button type="submit" className="btn btn-primary" disabled={!isOwner || !versions || !all || busy}>
        {busy ? <Spinner size={16} /> : null}
        <FormattedMessage id="onboarding.terms.accept" />
      </button>
    </form>
  );
}

export function PlanPicker({ isOwner, ctaId = "onboarding.plan.choose" }) {
  const errMsg = useErrorMessage();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    endpoints.plans().then((d) => setPlans(d.plans || []), setError);
  }, []);

  const choose = async (planId) => {
    setBusy(planId);
    setError(null);
    try {
      const { url } = await endpoints.checkout(planId);
      window.location.assign(url);
    } catch (err) {
      setError(err);
      setBusy(null);
    }
  };

  return (
    <>
      <ErrorBanner message={error ? errMsg(error) : null} />
      {!isOwner ? (
        <Banner kind="warn" icon="lock">
          <FormattedMessage id="onboarding.ownerOnly" />
        </Banner>
      ) : null}
      {!plans && !error ? <Loading /> : null}
      {plans ? (
        <div className="plan-grid">
          {plans.map((p) => (
            <div key={p.id} className={`plan-card ${p.id === "team" ? "is-featured" : ""}`}>
              {p.id === "team" ? (
                <span className="plan-badge">
                  <FormattedMessage id="onboarding.plan.popular" />
                </span>
              ) : null}
              <h3 className="plan-name">
                <FormattedMessage id={`plan.${p.id}`} defaultMessage={p.id} />
              </h3>
              <div className="plan-price">
                <FormattedNumber value={p.priceEur} style="currency" currency="EUR" maximumFractionDigits={0} />
                <span className="muted small">
                  <FormattedMessage id="onboarding.plan.perMonth" />
                </span>
              </div>
              <ul className="plan-feats">
                <li>
                  <Icon name="coins" size={15} />
                  <FormattedMessage id="onboarding.plan.credits" values={{ n: p.monthlyCredits }} />
                </li>
                <li>
                  <Icon name="users" size={15} />
                  <FormattedMessage id="onboarding.plan.seats" values={{ n: p.seats }} />
                </li>
                <li>
                  <Icon name="clock" size={15} />
                  <FormattedMessage id="onboarding.plan.retention" values={{ n: p.retentionDays }} />
                </li>
                <li className={p.tierB ? "" : "is-off"}>
                  <Icon name={p.tierB ? "shield" : "lock"} size={15} />
                  <FormattedMessage id={p.tierB ? "onboarding.plan.tierB" : "onboarding.plan.tierAOnly"} />
                </li>
              </ul>
              <button
                type="button"
                className={`btn btn-block ${p.id === "team" ? "btn-primary" : "btn-outline"}`}
                disabled={!isOwner || Boolean(busy)}
                onClick={() => choose(p.id)}
              >
                {busy === p.id ? <Spinner size={16} /> : null}
                <FormattedMessage id={ctaId} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export default function Onboarding() {
  useDocumentTitle("onboarding.title");
  const lp = useLocalePath();
  const { memberships, tenant, role, refreshMe, setTenant } = useSession();
  const derived = !memberships.length ? "org" : !tenant?.termsAcceptedAt ? "terms" : "plan";
  const [step, setStep] = useState(derived);
  const isOwner = role === "owner";

  // Already subscribed: nothing to onboard.
  if (
    memberships.length &&
    tenant &&
    !["verify_email", "accept_terms", "subscribe"].includes(tenant.nextVerificationStep)
  ) {
    return <Navigate to={lp("/")} replace />;
  }

  return (
    <AuthShell wide={step === "plan"}>
      <div className={`card auth-card ${step === "plan" ? "is-wide" : ""}`}>
        <Stepper current={step} />
        {step === "org" ? (
          <OrgStep
            onDone={async () => {
              await refreshMe();
              setStep("terms");
            }}
          />
        ) : null}
        {step === "terms" ? (
          <TermsStep
            isOwner={isOwner}
            onDone={async (t) => {
              setTenant(t);
              setStep("plan");
            }}
          />
        ) : null}
        {step === "plan" ? (
          <div className="form">
            <h2 className="h2">
              <FormattedMessage id="onboarding.plan.title" />
            </h2>
            <p className="muted">
              <FormattedMessage id="onboarding.plan.body" />
            </p>
            <PlanPicker isOwner={isOwner} />
          </div>
        ) : null}
      </div>
    </AuthShell>
  );
}
